import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, catchError, map, of, tap } from 'rxjs';
import { environment } from 'environments/environment';

export interface WorkOSUser {
    id: string;
    email: string;
    first_name?: string;
    last_name?: string;
    firstName?: string;
    lastName?: string;
    access_token?: string;
    authenticated_at?: string;
}

@Injectable({
    providedIn: 'root'
})
export class WorkOSService {
    private http = inject(HttpClient);

    private _currentUser = new BehaviorSubject<WorkOSUser | null>(null);
    private _authenticated = new BehaviorSubject<boolean>(false);

    public currentUser$ = this._currentUser.asObservable();
    public authenticated$ = this._authenticated.asObservable();

    /**
     * Initialize WorkOS - check for existing session
     */
    initialize(): Observable<boolean> {
        console.log('🔵 WorkOSService.initialize() called');
        return this.getUser().pipe(
            map(response => {
                console.log('🟢 WorkOSService.initialize response:', response);
                console.log('🟢 Response type:', typeof response);
                console.log('🟢 Response.user:', response?.user);
                if (response?.user) {
                    console.log('✅ User found, setting current user:', response.user);
                    this._currentUser.next(response.user);
                    this._authenticated.next(true);
                    console.log('✅ Current user state after set:', this._currentUser.value);
                    return true;
                }
                console.log('❌ No user in response');
                return false;
            }),
            catchError((error) => {
                console.error('❌ WorkOSService.initialize error:', error);
                this._currentUser.next(null);
                this._authenticated.next(false);
                return of(false);
            })
        );
    }

    /**
     * Get current authenticated user from backend
     */
    getUser(): Observable<{ user: WorkOSUser }> {
        return this.http.get<{ user: WorkOSUser }>('/api/auth/user', {
            withCredentials: true
        });
    }

    /**
     * Initiate WorkOS login - redirects to backend auth endpoint
     */
    login(): void {
        window.location.href = '/api/auth/login';
    }

    /**
     * Logout user - calls backend to revoke WorkOS session
     */
    logout(): void {
        // Navigate to backend logout endpoint which handles session revocation
        window.location.href = '/api/auth/logout';
    }

    /**
     * Check if user is authenticated
     */
    isAuthenticated(): boolean {
        return this._authenticated.value;
    }

    /**
     * Get current user synchronously
     */
    getCurrentUser(): WorkOSUser | null {
        return this._currentUser.value;
    }

    /**
     * Get user display name
     */
    getUserDisplayName(): string {
        const user = this._currentUser.value;
        // console.log('📛 getUserDisplayName() called');
        // console.log('📛 Current user:', user);
        // console.log('📛 User type:', typeof user);
        if (user) {
            // console.log('📛 User keys:', Object.keys(user));
            // console.log('📛 first_name:', user.first_name);
            // console.log('📛 firstName:', user.firstName);
            // console.log('📛 last_name:', user.last_name);
            // console.log('📛 lastName:', user.lastName);
            // console.log('📛 email:', user.email);
        }
        
        if (!user) {
            // console.log('📛 No user, returning empty string');
            return '';
        }
        
        const firstName = user.first_name || user.firstName || '';
        const lastName = user.last_name || user.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim();
        
        // console.log('📛 firstName:', firstName);
        // console.log('📛 lastName:', lastName);
        // console.log('📛 fullName:', fullName);
        // console.log('📛 Final result:', fullName || user.email);
        
        return fullName || user.email;
    }

    /**
     * Get user initials for avatar
     */
    getUserInitials(): string {
        const user = this._currentUser.value;
        if (!user) return '?';
        
        const firstName = user.first_name || user.firstName;
        const lastName = user.last_name || user.lastName;
        
        if (firstName && lastName) {
            return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase();
        } else if (firstName) {
            return firstName.charAt(0).toUpperCase();
        } else if (user.email) {
            return user.email.charAt(0).toUpperCase();
        }
        
        return '?';
    }

    /**
     * Handle OAuth callback (called after redirect from WorkOS)
     */
    handleCallback(code: string): Observable<boolean> {
        // Backend handles the code exchange, we just verify the session
        return this.getUser().pipe(
            map(response => {
                if (response?.user) {
                    this._currentUser.next(response.user);
                    this._authenticated.next(true);
                    return true;
                }
                return false;
            }),
            catchError(() => {
                this._currentUser.next(null);
                this._authenticated.next(false);
                return of(false);
            })
        );
    }

    /**
     * Refresh user session
     */
    refreshSession(): Observable<boolean> {
        return this.initialize();
    }
}
